import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  EDUCATION_FOCUS_REDIRECT,
  isRidesModuleEnabled,
  isSportsModuleEnabled,
} from '../../config/modules';

interface PrivateRouteProps {
  requiredRole?: 'TEACHER' | 'STUDENT' | 'SELLER' | 'SPORTS' | 'RIDES' | 'ADMIN';
  children?: React.ReactNode;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ requiredRole, children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const normalizedUser: any = (user as any)?.user ? (user as any).user : user;

  if (loading) {
    return <div>Loading...</div>; // Or a loading spinner
  }

  if (!normalizedUser) {
    const nextPath = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`/login?next=${encodeURIComponent(nextPath)}`} replace />;
  }

  // If no specific role is required, allow access to the requested route
  if (!requiredRole) {
    return <>{children || <Outlet />}</>;
  }

  // Check if user has the required role
  if (normalizedUser.role !== requiredRole) {
    // Redirect to role-specific dashboard if trying to access a protected route without permission
    const dashboardPath =
      normalizedUser.role === 'TEACHER'
        ? '/teacher/dashboard'
        : normalizedUser.role === 'SELLER'
        ? '/shop/dashboard'
        : normalizedUser.role === 'SPORTS'
        ? isSportsModuleEnabled
          ? '/sports/dashboard'
          : EDUCATION_FOCUS_REDIRECT
        : normalizedUser.role === 'RIDES'
        ? isRidesModuleEnabled
          ? '/rides/dashboard'
          : EDUCATION_FOCUS_REDIRECT
        : normalizedUser.role === 'ADMIN'
        ? '/admin/schedule-dashboard'
        : '/student/dashboard';
    return <Navigate to={dashboardPath} replace />;
  }

  // If user has the required role, render the requested content
  return <>{children || <Outlet />}</>;
};

export default PrivateRoute;
